import React from 'react';

import { Box, Divider, Grid, Typography } from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
    ReactHookFormIntlNumberInput,
    ReactHookFormToggleButtonGroup,
} from 'common/components/form/bootstrap';
import {
    getMinMaxCols,
    getMinMaxGroupTilt,
    getMinMaxRows,
} from 'common/utils/helpers/systemsValues';

import info1 from 'resources/info_1.png';
import info2 from 'resources/info_2.png';
import info3 from 'resources/info_3.png';

import InfoPopover from '../../InfoPopover';

const TabGroups = ({ control }) => {
    const { t } = useTranslation();
    const [system_uid, panel] = useWatch({
        control,
        name: ['system_uid', 'panel'],
    });

    const [minCols, maxCols] = getMinMaxCols(system_uid, panel.width);
    const [minTilt, maxTilt] = getMinMaxGroupTilt(system_uid);
    const [minRows, maxRows] = getMinMaxRows(system_uid);

    const disabledCols = minCols === maxCols;
    const disabledRows = minRows === maxRows;
    const disabledTilt = minTilt === maxTilt || maxTilt === 0;

    return (
        <>
            <ReactHookFormIntlNumberInput
                append="°"
                control={control}
                disabled={disabledTilt}
                fullWidth
                label={t('Tilt')}
                max="90"
                min="0"
                name="tilt"
                placeholder={t('Tilt')}
            />

            <ReactHookFormToggleButtonGroup
                control={control}
                fullWidth
                name="algorithm"
                options={[
                    { label: t('Dynamic'), value: 'dynamic' },
                    { label: t('Strict'), value: 'strict' },
                ]}
            />

            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                <Typography fontWeight="bold" variant="caption">
                    {t('Panel group dimension')}
                </Typography>

                <InfoPopover title={t('Group size')} image={info2} />
            </Box>

            <Divider />

            <Grid container mt={1}>
                <Grid size={{ md: 9, xs: 18 }}>
                    <ReactHookFormIntlNumberInput
                        control={control}
                        disabled={disabledRows}
                        fullWidth
                        label={t('Row', {
                            count: 2,
                        })}
                        name="frame_rows_number"
                    />
                </Grid>

                <Grid size={{ md: 9, xs: 18 }}>
                    <ReactHookFormIntlNumberInput
                        control={control}
                        disabled={disabledCols}
                        fullWidth
                        label={t('Column', {
                            count: 2,
                        })}
                        name="frame_cols_number"
                    />
                </Grid>
            </Grid>

            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                <Typography variant="caption" fontWeight="bold">
                    {t('Distance between groups')}
                </Typography>

                <InfoPopover
                    title={t('Distance between groups')}
                    image={info3}
                />
            </Box>

            <Divider />

            <Grid container mt={1}>
                <Grid size={{ md: 9, xs: 18 }}>
                    <ReactHookFormIntlNumberInput
                        append="m"
                        control={control}
                        fullWidth
                        label={t('Vertical')}
                        name="frame_row_spacing"
                    />
                </Grid>

                <Grid size={{ md: 9, xs: 18 }}>
                    <ReactHookFormIntlNumberInput
                        append="m"
                        control={control}
                        fullWidth
                        label={t('Horizontal')}
                        name="frame_col_spacing"
                    />
                </Grid>
            </Grid>

            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                <Typography variant="caption" fontWeight="bold">
                    {t('Distance between panels')}
                </Typography>

                <InfoPopover
                    title={t('Distance between panels')}
                    image={info1}
                />
            </Box>

            <Divider />

            <Grid container mt={1}>
                <Grid size={{ md: 9, xs: 18 }}>
                    <ReactHookFormIntlNumberInput
                        append="m"
                        control={control}
                        disabled
                        fullWidth
                        label={t('Vertical')}
                        name="row_spacing"
                    />
                </Grid>

                <Grid size={{ md: 9, xs: 18 }}>
                    <ReactHookFormIntlNumberInput
                        append="m"
                        control={control}
                        disabled
                        fullWidth
                        label={t('Horizontal')}
                        name="col_spacing"
                    />
                </Grid>
            </Grid>
        </>
    );
};

TabGroups.propTypes = { control: PropTypes.object };

export default TabGroups;
